import { Component, OnInit, Input, Inject, ViewChild } from '@angular/core';
import { ActionService } from 'src/app/services/action.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserSelectorDlgComponent } from 'src/app/chat/dialogs/user-selector-dlg/user-selector-dlg.component';
import {
    EUserSelectorDlgResultStatus,
    IUserSelectorSettings,
    IUserSelectorDlgResult,
} from 'src/app/chat/dialogs/user-selector-dlg/user-selector-dlg.interface';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DefaultUserData, IUser, UserAccountType } from 'src/app/data-types/user-data';
import { UserService } from 'src/app/services/user.service';
import { ToastrService } from 'ngx-toastr';
import { ActionTypeLabel, IAction, IActionFormData } from '../model/action.model';
import { Store } from 'src/app/store';
import { NoteFormComponent } from '../note-form/note-form.component';
import { INote } from 'src/app/data-types/note-data';
import { NotesService } from 'src/app/services/notes.service';
import { NoteType } from 'src/app/models/notes';

interface IActionType {
    id: number;
    name: string;
    label: ActionTypeLabel;
    isGroup: 0 | 1;
    types?: IActionType[];
}

@UntilDestroy()
@Component({
    selector: 'app-create-action-form',
    templateUrl: './create-action-form.component.html',
    styleUrls: ['./create-action-form.component.scss'],
})
export class CreateActionFormComponent implements OnInit {
    @Input() action: IAction = {
        actionType: null,
        allocatedTo: null,
        completionTime: null,
        customer: null,
        products: [],
        taggedAdminsIds: [],
    } as IAction;
    ActionTypeLabel = ActionTypeLabel;

    @ViewChild(NoteFormComponent)
    noteFormComponent: NoteFormComponent;

    groups: IActionType[];
    types: IActionType[];
    form: FormGroup;
    selectedAdmin = null;
    formStep: 1 | 2 = 1;
    nextStepDisabled = true;
    subFormIsLoading$: Subject<boolean> = new BehaviorSubject(true);
    showDateTimePicker: boolean = false;
    updateCustomerConfirmationData = {
        open: false,
        buttonText: 'Confirm',
        title: '',
        detail: '',
        data: undefined,
    };
    step1Loading = false;
    resolutionScreen = false;
    showHeaderBtn = false;
    notes: INote[];
    notesLoaded = false;
    NoteType = NoteType;

    constructor(
        private actionService: ActionService,
        private fb: FormBuilder,
        private userService: UserService,
        public dialog: MatDialog,
        public dialogRef: MatDialogRef<UserSelectorDlgComponent>,
        public dlgRef: MatDialogRef<CreateActionFormComponent>,
        public toastrService: ToastrService,
        private store: Store,
        private notesService: NotesService,
        @Inject(MAT_DIALOG_DATA) public data: { action: IAction; resolutionScreen?: boolean }
    ) {}

    ngOnInit(): void {
        if (this.data?.action?.id) {
            this.setupStep2();
        } else {
            this.step1Loading = true;
            this.actionService
                .getAdminActionTypes()
                .pipe(untilDestroyed(this))
                .subscribe((groups) => {
                    this.groups = groups;
                    this.step1Loading = false;
                });
        }

        this.form = this.fb.group({
            selectedGroupId: [null],
            actionType: [this.action.actionType, [Validators.required]],
            allocatedTo: [this.action.allocatedTo?.id, [Validators.required]],
            completionTime: [this.action.completionTime],
            taggedAdminsIds: [this.action.taggedAdminsIds],
        });

        if (this.resolutionScreen) {
            this.form.addControl('resolution', new FormControl(this.action.resolution, Validators.required));
        }

        this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            if (this.form.get('selectedGroupId').valid && this.form.get('actionType').valid) {
                this.nextStepDisabled = false;
            } else {
                this.nextStepDisabled = true;
            }
            const selectedGroupId = this.form.get('selectedGroupId').value;
            const selectedTypeId = this.form.get('actionType').value;
            if (this.groups && selectedGroupId && selectedTypeId) {
                const group = this.groups.find((g) => g.id === selectedGroupId);
                if (group.isGroup === 0) {
                    this.action.actionTypeName = group.name;
                    this.action.actionTypeLabel = group.label;
                } else {
                    const type = group.types.find((t) => t.id === selectedTypeId);
                    this.action.actionTypeName = type.name;
                    this.action.actionTypeLabel = type.label;
                }

                if (this.action.actionTypeLabel === ActionTypeLabel.ORD_QUER) {
                    this.showHeaderBtn = true;
                }
            }
        });

        // set current user as assigned admin by default
        if (this.formStep === 1 && !this.form.get('allocatedTo').value) {
            this.setCurrentUserAssignedAdmin();
        }
    }

    goToNextFormStep() {
        this.formStep = 2;
        this.dlgRef.addPanelClass('create-action-form-wide');
    }

    showSelectAdminDlg() {
        this.dialog
            .open(UserSelectorDlgComponent, {
                height: '616px',
                width: '390px',
                position: { top: '305px' },
                data: {
                    mode: 'single',
                    allowedUserTypes: [UserAccountType.Admin],
                    showTypeSelector: false,
                } as IUserSelectorSettings,
            })
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((data: IUserSelectorDlgResult) => {
                if (data?.status === EUserSelectorDlgResultStatus.Selected) {
                    this.selectedAdmin = data.members;
                    this.form.get('allocatedTo').setValue(this.selectedAdmin.id);
                }
            });
    }

    async saveAction() {
        const payload = this.form.value;
        if (!payload.customerId && payload.newCustomer) {
            const customer = { ...DefaultUserData, prospect: 1, ...payload.newCustomer };
            customer.username = payload.newCustomer.email;
            customer.contact.firstname = payload.newCustomer.firstname;
            customer.contact.surname = payload.newCustomer.surname;
            customer.contact.email = payload.newCustomer.email;
            customer.contact.mobile = payload.newCustomer.phone?.e164Number;

            const params = {
                email: customer.contact.email as string,
                mobile: customer.contact.mobile as string,
            };

            const checkingResult = (await firstValueFrom(this.userService.checkUserExist(params))) as any;

            if (checkingResult.data) {
                const existsUser: any = checkingResult.data;
                customer.id = existsUser.id;
                customer.contact = {
                    ...existsUser.contact,
                    ...customer.contact,
                };
                this.confirmCustomerUpdate(customer, payload, existsUser);
                return;
            } else {
                const createdUser = await this.userService.createUserNew(customer);
                payload.customerId = createdUser.id;
            }
        } else if (payload.customerId && payload.newCustomer.modified) {
            const existsUser = (await firstValueFrom(this.userService.findOne(payload.customerId))).data;
            const customer: any = {
                ...existsUser,
                username: payload.newCustomer.email,
                contact: {
                    firstname: payload.newCustomer.firstname,
                    surname: payload.newCustomer.surname,
                    email: payload.newCustomer.email,
                    mobile: payload.newCustomer.phone?.e164Number,
                },
            };
            const updateReuslt = await firstValueFrom(this.userService.update(customer));
        }

        if (this.resolutionScreen && this.form.get('resolution').value) {
            payload.status = 4;
        }

        if (payload.phoneOptional) {
            payload.phoneOptional = payload.phoneOptional.e164Number;
        }

        this.createOrUpdateAction(payload);
    }

    close(modified = false) {
        this.dialogRef.close({ modified, action: this.action });
    }

    onGroupSelected() {
        const selectedGroupId: number = this.form.get('selectedGroupId').value;
        const selectedGroupOrType: IActionType = this.groups.find((g) => g.id === selectedGroupId);
        if (!selectedGroupOrType.isGroup) {
            this.types = [selectedGroupOrType];
            this.form.get('actionType').setValue(selectedGroupOrType.id);
        } else {
            this.types = selectedGroupOrType.types;
            this.form.get('actionType').setValue(null);
        }
    }

    removeAdmin() {
        this.selectedAdmin = null;
        this.form.get('allocatedTo').setValue(null);
    }

    removeCompletionTime() {
        this.action.completionTime = null;
    }

    datetimeSelected(datetime: Date) {
        this.action.completionTime = datetime;
        this.showDateTimePicker = false;
        this.form.get('completionTime').setValue(datetime);
    }

    closeDatePicker() {
        this.showDateTimePicker = false;
    }

    confirmCustomerUpdate(customer, payload, existsUser) {
        this.updateCustomerConfirmationData.title = `User ${existsUser.contact.firstname} ${existsUser.contact.surname} with email ${existsUser.contact.email} and phone ${existsUser.contact.mobile} already exists. Do you want to override it?`;
        this.updateCustomerConfirmationData.data = { customer, payload };
        this.updateCustomerConfirmationData.detail = '';
        this.updateCustomerConfirmationData.open = true;
    }

    async confirmAction(e) {
        this.updateCustomerConfirmationData.open = false;
        if (e.action) {
            const customer = this.updateCustomerConfirmationData.data.customer;
            const payload = this.updateCustomerConfirmationData.data.payload;
            const updateReuslt = await firstValueFrom(this.userService.update(customer));
            payload.customerId = updateReuslt.data.id;
            this.createOrUpdateAction(payload);
        }
    }

    userSelected(selectedAdmins: IUser[]) {
        const taggedAdminsIds = selectedAdmins.map((a) => a.id);
        this.form.get('taggedAdminsIds').setValue(taggedAdminsIds);
    }

    getNotes() {
        this.notesService
            .getNotesFor('action', this.action.id.toString())
            .pipe(untilDestroyed(this))
            .subscribe((data) => {
                this.notes = data;
                this.notesLoaded = true;
            });
    }

    addNote() {
        this.noteFormComponent.showForm();
    }

    noteAdded(note: INote) {
        this.notes.unshift(note);
    }

    private createOrUpdateAction(payload) {
        if (this.action.id) {
            this.updateAction(payload);
        } else {
            this.createAction(payload);
        }
    }

    private createAction(payload) {
        this.actionService.createAction(payload).subscribe();
        this.close();
    }

    private updateAction(actionData: IActionFormData) {
        actionData.id = this.action.id;
        this.actionService.updateAction(actionData).subscribe((updatedAction: IAction) => {
            for (const [key, value] of Object.entries(updatedAction)) {
                this.action[key] = value;
            }
            this.toastrService.success('Action updated');
            this.close(true);
        });
    }

    private setupStep2() {
        this.action = this.data.action;
        if (this.action.allocatedTo) {
            this.selectedAdmin = this.action.allocatedTo;
            this.selectedAdmin.contact = {
                firstname: this.action.allocatedTo.firstname,
                surname: this.action.allocatedTo.surname,
            };
        }

        this.resolutionScreen = !!this.data.resolutionScreen || this.action.status.id === 4;

        this.getNotes();

        this.goToNextFormStep();
    }

    private setCurrentUserAssignedAdmin() {
        const currentUser = this.store.selectForLocal('user');
        this.selectedAdmin = currentUser;
        this.form.get('allocatedTo').setValue(currentUser.id);
    }
}
